<script lang="ts" context="module">
  const current = writable<Record<string, string>>({});
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { scale, slide } from 'svelte/transition';
  import { circInOut } from 'svelte/easing';
  import { cva } from 'class-variance-authority';

  import Badge from './ui/badge.svelte';
  import Icon, { type IconOptions } from './ui/icon/icon.svelte';
  import SplitLines from './split-lines.svelte';

  import { cn } from '$lib/utils';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { getUUID } from '$lib/utils/uuid';

  let className: string | undefined = undefined;
  let id = getUUID();

  export { className as class };
  /**
   * Accordion group identifier. All accordions with the same group will be exclusive.
   * This means that only one accordion can be expanded at a time.
   * @default id
   * @type {string}
   */
  export let group: string = id;
  export let title: string | undefined = undefined;
  export let preview: string | undefined = undefined;
  export let as: 'p' | 'h2' | 'h3' | 'h4' | 'h5' = 'p';
  export let variant:
    | 'default'
    | 'product'
    | 'image-info'
    | 'filters'
    | 'footer'
    | 'discount'
    | 'header' = 'default';
  export let icon: IconOptions | undefined = undefined;
  export let renderMode: 'always' | 'when-expanded' = 'when-expanded';
  export let badge: string | undefined = undefined;
  export let initialOpen: boolean | undefined = undefined;
  export let onInteract: (() => void) | undefined = undefined;
  export let titleColor: string | undefined = undefined;

  onMount(() => {
    if (initialOpen) {
      toggle();
    }
  });

  const toggle = () => {
    setTimeout(() => {
      initialOpen = undefined;
    }, 300);
    current.update((curr) => {
      const { [group]: currentGroup, ...rest } = curr;
      return currentGroup === id ? rest : { ...rest, [group]: id };
    });
  };

  $: expanded = $current[group] === id;

  let hasInteracted = false;
  let height: number;

  const host = cva(['relative overflow-hidden border-primary/4'], {
    variants: {
      variant: {
        default: 'border-b',
        product: 'border bg-primary/2 rounded-sm',
        discount: 'border bg-primary/2 rounded-sm',
        'image-info': 'border bg-highlight/88 rounded-sm',
        filters: 'border-b first:border-t',
        footer: 'border-b border-primary/8',
        header: 'border-b border-primary/8'
      }
    }
  });

  const trigger = cva(['font-semibold flex w-full items-center justify-between'], {
    variants: {
      variant: {
        default: 'p-4 text-md',
        product: 'p-3 text-sm',
        discount: 'p-3 text-sm',
        'image-info': 'px-2.5 py-1.5 text-xs font-semibold gap-1.5',
        filters: 'py-4 px-0 text-md font-medium',
        footer: 'p-4 text-md font-medium',
        header: 'px-0 pt-4 pb-3 text-lg font-medium'
      }
    }
  });
</script>

<div class={cn(host({ variant }), className)} {...$$restProps}>
  <button
    aria-label="accordion"
    aria-expanded={expanded}
    id={`accordion-header-${id}`}
    aria-controls={`accordion-panel-${id}`}
    class={cn(trigger({ variant }))}
    type="button"
    on:click={() => {
      toggle();

      if (!hasInteracted && onInteract) {
        hasInteracted = true;
        onInteract();
      }
    }}
  >
    <svelte:element this={as} class="flex items-center gap-2 text-left">
      {#if icon}
        <Icon size="md" {icon} class="block h-fit shrink-0" />
      {/if}
      <div style:--titleColor={titleColor}>
        <slot name="title">
          <span
            class={cn(
              'leading-none',
              variant === 'discount' && 'font-medium',
              variant === 'header' && titleColor && 'text-[--titleColor]'
            )}>{title}</span
          >
        </slot>

        {#if preview}
          <SplitLines class="leading-none text-primary/40 mt-1" text={preview} />
        {/if}
      </div>
      {#if badge}
        <div transition:scale={{ duration: $reducedMotion ? 0 : 100 }}>
          <Badge size="micro">{badge}</Badge>
        </div>
      {/if}
    </svelte:element>

    <div class="flex items-center gap-2">
      <slot name="action" />

      {#if variant !== 'default' && variant !== 'discount'}
        <Icon
          size={variant === 'image-info' ? 'sm' : 'md'}
          icon={variant === 'image-info' ? 'chevron-right-bold' : 'chevron-right'}
          class={cn(
            !initialOpen && 'transition-transform duration-300 motion-reduce:transition-none',
            expanded ? '-rotate-90' : 'rotate-90',
            variant === 'header' && 'text-primary/60'
          )}
        />
      {/if}

      {#if variant === 'default' || variant === 'discount'}
        {#if expanded}
          <Icon size={variant === 'discount' ? 'sm' : 'md'} icon="minus" class="shrink-0" />
        {:else}
          <Icon size={variant === 'discount' ? 'sm' : 'md'} icon="plus" class="shrink-0" />
        {/if}
      {/if}
    </div>
  </button>

  <div
    id={`accordion-panel-${id}`}
    aria-labelledby={`accordion-header-${id}`}
    data-render-mode={renderMode}
    data-initial-open={!!initialOpen}
    style={`--accordion-content-height: ${expanded ? `${height}px` : '0px'}`}
  >
    {#if renderMode === 'always'}
      <div
        class={cn(
          'flex flex-col px-4 pb-4',
          variant === 'product' && 'px-3 pb-3 pt-5',
          variant === 'image-info' && 'px-3 pb-3',
          variant === 'filters' && 'px-0 pb-4'
        )}
        bind:clientHeight={height}
      >
        <slot />
      </div>
    {:else if expanded}
      <div
        class={cn(
          'flex flex-col px-4 pb-4',
          variant === 'product' && 'px-3 pb-3 pt-5',
          variant === 'image-info' && 'px-3 pb-3',
          variant === 'filters' && 'px-0 pb-4',
          variant === 'footer' && 'pt-4 pb-8',
          variant === 'discount' && 'pt-5 px-3',
          variant === 'header' && 'px-0 pt-2 pb-3'
        )}
        transition:slide|local={{
          duration: $reducedMotion ? 0 : 300,
          easing: circInOut
        }}
      >
        <slot />
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  div[data-render-mode='always'][data-initial-open='false'] {
    max-height: var(--accordion-content-height);
    @media (prefers-reduced-motion: no-preference) {
      transition: max-height 300ms cubic-bezier(0.85, 0.18, 0.18, 0.85);
    }
  }
</style>
